import {PolymerElement, html} from '@polymer/polymer/polymer-element.js';
import '@polymer/paper-button/paper-button.js';
import '@polymer/paper-dialog/paper-dialog.js';
import '@material/web/dialog/dialog.js';
import '@cbar/cbar-shared-styles/cbar-shared-styles.js';
import '@polymer/iron-icons/image-icons.js';
import '@polymer/app-layout/app-layout.js';
import '@polymer/paper-icon-button/paper-icon-button.js';
import '@polymer/iron-icons/iron-icons.js';
import '@polymer/paper-tabs/paper-tabs.js';
import '@polymer/paper-spinner/paper-spinner.js';
import '@polymer/iron-pages/iron-pages.js';
import '@cbar/file-drop-zone/file-drop-zone.js';
import '@cbar/yo-paper-cropper';

/**
 * `cbar-photo-uploader` enables uploading images and cropping them for use in
 * your apps, this allows for a user to select an image from an existing set or
 * upload one directly from their computer, this is done through 2 other
 * elements `file-drop-zone` as well as `yo-paper-cropper`.
 *
 * The element is a paper-dialog containing paper-tabs, iron-pages and the
 * file-drop-zone elements, this allows for a user to upload an image by
 * dragging or clicking on the element and selecting a file from their
 * device/desktop, after doing such the element will take them to a cropper
 * which will output the cropped image for use.
 *
 * After the image has been cropped the user will press the "update" button and
 * the image will be saved. Whatever you wish to do with this image is
 * controlled externally through the uploading-changed event which is on the
 * notify property "uploading"
 *
 * TODO: allow for the viewing of socially linked accounts
 *
 * @customElement
 * @polymer
 * @demo demo/index.html
 */
class CbarPhotoUploader extends PolymerElement {
  static get template() {
    return html`
        <style include="cbar-shared-styles">
            :host {
                display: block;
            }

            file-drop-zone {
                width: 95%;
                height: 50%;
                background-color: white;
                transition: all .3s;
                border: 1px dashed transparent;
            }

            md-dialog {
                width: 60%;
                max-height: 500px;
            }

            file-drop-zone.dragover {
                border: 1px dashed var(--cbar-sys-color-primary, #004CA2);
                transition: all .3s;
            }

            file-drop-zone:hover > [slot='drop-zone'],
            file-drop-zone.dragover > [slot='drop-zone'] {
                color: var(--cbar-sys-color-primary, #004CA2);
                transition: all .3s;
            }

            file-drop-zone.errored {
                background-color: #FFEBEE;
                transition: all .3s;
            }

            file-drop-zone[has-files] {
                color: var(--cbar-sys-color-secondary, var(--cbar-dark-primary-color, #003F83));
                transition: all .3s;
            }

            [slot='drop-zone'] {
                text-align: center;
                font-size: 1.1em;
                --iron-icon-height: 64px;
                --iron-icon-width: 64px;
            }

            [slot='drop-zone'] > .title {
                font-size: 1.2em;
                font-weight: 200;
            }

            [slot='drop-zone'] > .small {
                font-size: 0.6em;
            }

            .body-display-container {
                padding: 0;
            }

            .image-preview {
                display: none;
                width: 95%;
                height: 95%;
                margin: 8px 0;
            }

            app-header, paper-tabs {
                margin: 0;
                padding: 0;
                color: black;
            }

            paper-tabs {
                border-bottom: 1px solid #eeeeee;
            }

            iron-pages {
                height: calc(100% - 157px);
            }

            #edit {
                height: 100%;
                margin-top: -20px;
            }

            #back-icon {
                align-self: left;
            }

            paper-tabs {
                background-color: white;
            }

            paper-button[disabled] {
                background-color: #e8e8e8;
            }

            paper-spinner {
                margin-right: 5px;
            }

            .buttons-div {
                display: flex;
                align-items: center;
            }

            @media (min-width: 0px) and (max-width: 500px) {
                paper-dialog {
                    width: 100%;
                    height: 500px;
                }

            }
        </style>
        <md-dialog id="dialog" with-backdrop="" scroll-action="lock" open="{{open}}" on-close="__closeHandler">
            <app-header reveals="" fixed="" slot="headline">
                <app-toolbar>
                    <template is="dom-if" if="[[__showBackButton]]">
                        <paper-icon-button style="padding: 0; height: 24px; width: 24px; margin-right: 8px" icon="chevron-left" id="back-icon" on-tap="_backToUpload"></paper-icon-button>
                    </template>
                    <div main-title="" class="slim-font">[[_status]]</div>
                    <paper-icon-button icon="close" on-tap="_closeModal"></paper-icon-button>
                </app-toolbar>
            </app-header>
            <div slot="content" style="padding: 0 !important;">
                <template is="dom-if" if="[[!social]]">
                    <paper-tabs id="tabsDiv" selected="{{_selectedPage}}" attr-for-selected="name">
                        <paper-tab name="upload">Upload</paper-tab>
                        <paper-tab name="social" hidden$="[[!social]]">Social Images</paper-tab>
                    </paper-tabs>
                </template>

                <iron-pages selected="{{_selectedPage}}" attr-for-selected="name" fallback-selection="upload">
                    <div class="body-display-container" style="height: 351px" name="upload">
                        <file-drop-zone accept="image/*" files="{{files}}" on-change="_editImage" id="imageInput">
                            <div slot="drop-zone">
                                <iron-icon icon="image:photo-library"></iron-icon>
                                <div class="title">Drop Your Image Or Click</div>
                            </div>
                        </file-drop-zone>
                    </div>
                    <div id="edit" name="edit">
                        <div class="body-display-container">
                            <yo-paper-cropper class="image-preview"></yo-paper-cropper>
                        </div>
                    </div>
                </iron-pages>
            </div>

            <div class="buttons buttons-div" slot="actions">
                <template is="dom-if" if="[[__showBackButton]]">
                    <div style="color: red">[[errorMessage]]</div>
                    <paper-spinner active$="[[__loading]]"></paper-spinner>
                    <paper-button on-tap="_closeModal" disabled$="[[__loading]]">Cancel</paper-button>
                    <paper-button on-tap="_updatePhoto" disabled$="[[__loading]]">Update</paper-button>
                </template>
            </div>
        </md-dialog>
    `;
  }

  static get is() {
    return 'cbar-photo-uploader';
  }

  static get properties() {
    return {
      /**
       * The property that is used in the polymer tests to be developed for the
       * element TODO: make tests for elements
       */
      _name: {
        type: String,
        value: 'cbar-photo-uploader'
      },
      /**
       * Determines whether the modal should be opened, controlled from the
       * outside, or could be set to open whenever.
       */
      open: {
        type: Boolean,
        observer: "_checkOpen"
      },
      /**
       * Allows for the hiding or showing of the social tab/page, if the user
       * wishes for this functionaility to be hidden or not shown, don't pass
       * this.
       */
      social: {
        type: Boolean,
        value: false
      },
      /**
       * Determines the text on the top of the dialog
       */
      _status: {
        type: String,
        value: "Select a Photo"
      },
      type: {
        type: String,
        value: false,
        observer: "_determineAspectRatio"
      },
      initialCropArea: {
        type: Number,
        value: 0.8
      },
      __loading: {
        type: Boolean,
        value: false
      },
      maxWidth: {
        type: Number,
        value: 0
      },
      maxHeight: {
        type: Number,
        value: 0
      },
      errorMessage: {
        type: String,
        value: ""
      },
    };
  }

  static get observers() {
    return [
      'checkBackButton(_selectedPage)'
    ]
  }

  /**
   * Gets the cropper element in this if the variable is not set, if it is, will
   * return the existing cropper element.
   * @return {_Element | * | Element | any | Element}
   */
  get cropper() {
    if (!this._cropper) {
      this._cropper = this.root.querySelector(".image-preview");
    }
    return this._cropper;
  }


  /**
   * Returns the tabs of the dialog if the tabs element does not exist, if it
   * does, then we return the existing value.
   * @return {_Element | * | Element | any | Element}
   */
  get tabs() {
    if (!this._tabs) {
      this._tabs = this.root.querySelector("#tabsDiv");
    }
    return this._tabs
  }


  /**
   *  Ready listener on the element, sets the uploading property to false on
   *  load, as we assume the user is not uploading something when the element is
   *  loading. This also sets the cropper options when the element is loaded to
   *  make sure the settings that are desired are set in this context.
   */
  ready() {
    super.ready();
    if (this._selectedPage === "edit") {
      this.cropper.options = {
        dragMode: "move",
        aspectRatio: 9 / 9
      };
    }
  }

  checkBackButton(page) {
    this.__showBackButton = page === "edit";
  }


  /**
   * Function that determines what the aspect ratio of the cropbox should be in
   * this element, if it is a profile image, then we set it to 1/1 if it is a
   * cover image we set it to 16/9 anything else is free aspectRatio.
   */
  _determineAspectRatio(type) {
    if (!type || type !== "profile" && type !== "cover") {
      return;
    }
    let aspectRatio = "";
    if (type === "profile") {
      aspectRatio = 1;
    }
    if (type === "cover") {
      aspectRatio = 16 / 9;
    }
    this.aspectRatio = aspectRatio;
  }

  /**
   * Check if the open property is true, if it is, trigger the opening of the
   * dialog.
   * @private
   */
  _checkOpen(open) {
    this.errorMessage = "";
    this.open = open;
  }

  /**
   * When the image for editing is changed, we want the element to change pages
   * to the edit page for the image, this allows for the user to edit the image
   * and all of the describing test in the dialog changes to match the context.
   * @param e
   * @private
   */
  _editImage(e) {
    this.fileName = e.target.files[0].name;
    this._selectedPage = "edit";
    this.tabs.style.display = "none";
    this._status = "Edit Image";
    this.cropper.style.display = "flex";
    let url = URL.createObjectURL(e.target.files[0]);
    this.cropper.setAttribute('src', url);

    this.cropper.options = {
      aspectRatio: this.aspectRatio,
      autoCropArea: this.initialCropArea,
      dragMode: "move",
      minCropBoxWidth: 150,
      minCropBoxHeight: 150,
      maxCropBoxWidth: this.maxWidth,
      maxCropBoxHeight: this.maxHeight
    };
  }

  /**
   * Resets and closes the modal
   * @private
   */
  _closeModal() {
    this._backToUpload();
    this.$.dialog.close();
  }

  /**
   * Resets the modal to return to the front page of the modal, we have this in
   * order for the element to not bring the user back to where they were on
   * purpose.
   * @private
   */
  _backToUpload() {
    this.errorMessage = "";

    this.tabs.style.display = "block";
    this._selectedPage = "upload";
    this._status = "Select a Photo";
  }


  /**
   * When the update button is pressed, we allow for the uploaded-changed event
   * to be fired, and sets the image to the cropped image.
   * @private
   */
  _updatePhoto() {
    this.__loading = true;
    this.errorMessage = "";

    let cropboxData = this.cropper._cropper.getCropBoxData();
    let widthBiggerThanMax = this.maxWidth > 0 && cropboxData.width > this.maxWidth
    let heightBiggerThanMax = this.maxHeight > 0 && cropboxData.height > this.maxHeight

    let canvasProps = {};

    if (widthBiggerThanMax) {
      canvasProps.maxWidth = this.maxWidth
    }

    if (heightBiggerThanMax) {
      canvasProps.maxHeight = this.maxHeight;
    }

    let image = this.cropper._cropper.getCroppedCanvas(canvasProps);

    let cropper = this.cropper._cropper;
    let uploadEvent = new CustomEvent('uploaded-image', {
      bubbles: true,
      composed: true,
      cancelable: true,
      detail: {
        croppedCanvas: image,
        photoUploader: this,
        cropper,
        imageName: this.fileName
      }
    });

    this.dispatchEvent(uploadEvent);

    if (!uploadEvent.defaultPrevented) {
      this._closeModal();
      this.__loading = false;
    }
  }

  __closeHandler(e){
    if(this.__loading){
      e.preventDefault();
    } else {
      this.open = false;
    }
  }
}

window.customElements.define(CbarPhotoUploader.is, CbarPhotoUploader);
